import Style from './panelStyles.module.scss';
import clsx from 'clsx';
import React, { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNode } from '@craftjs/core';
import { ReactComponent as DragAndDrop } from 'assets/img/generalBuilder/drag_and_drop_24.svg';
import { ReactComponent as Delete } from 'assets/img/generalBuilder/delete_24.svg';
import { ReactComponent as Plus } from 'assets/img/generalBuilder/plus_20.svg';
import { DndProvider, DragSourceMonitor, useDrag, useDrop } from 'react-dnd';
import { useContextSelector } from 'use-context-selector';
import { isNullOrEmpty } from 'src/helpers/string-helper';
import { useScopedTranslation } from 'src/application/localisation/useScopedTranslation';
import { LayoutValidationContext, ValidationEnum } from '../../validationContext/layoutValidation.context';
import {
  FormFieldOption,
  FormFieldProps,
  FormFieldWithOptionsProps,
  FormFieldWithScoreProps,
  isFieldWithOptions,
} from '../../types/BaseControlTypes';
import { isScoreSectionsContainsDataKey, isVariableContainsDataKey } from '../../validationContext/helper';
import { PanelContainer } from './uiKit/panelContainer/panelContainer';
import { hasSection, ModifiedBackend } from './helpers/helper';
import { ControlsGroup } from './uiKit/controlsGroup/controlsGroup';
import { AppCheckboxInput } from 'uikit/inputs';
import { SettingsInput } from './uiKit/input/settingsInput';
import { createId } from 'src/helpers/typeUtils';
import { AppButton } from 'uikit/buttons';
import { ScoreContext } from 'src/features/generalForms/context/score.context';
import { defaultValueRenderers, getFieldType } from './helpers/answersPanelHelper';

type AnswersPanelSection = 'NoAnswerScore';

export type AnswersPanelProps = {
  sections?: AnswersPanelSection[];
};

export const AnswersPanel = ({ sections }: AnswersPanelProps) => {
  const { t } = useScopedTranslation('GeneralFormBuilder.Panels.Answers');
  const {
    id,
    actions: { setProp },
    props,
    nodeName,
  } = useNode((node) => ({
    props: node.data.props as FormFieldWithOptionsProps & FormFieldWithScoreProps,
    nodeName: node.data.name,
  }));

  const { scoreSections, variables } = useContextSelector(ScoreContext, (x) => x);

  const [scoreEnabled, setScoreEnabled] = useState(
    props.noAnswerScore !== undefined ||
      props.options?.some((x) => x.scoreWeight !== undefined) ||
      props.noAnswerScore !== undefined,
  );

  const validationError = useContextSelector(LayoutValidationContext, (x) => x.layoutValidationResult[id]);

  const optionWeightErrorMessage = useMemo(
    () => (validationError?.type === ValidationEnum.OptionWeight ? validationError?.message : undefined),
    [validationError?.message, validationError?.type],
  );

  const noAnswerScoreErrorMessage = useMemo(
    () => (validationError?.type === ValidationEnum.NoAnswerScore ? validationError?.message : undefined),
    [validationError?.message, validationError?.type],
  );

  const defaultValueComponent = useMemo(() => {
    const type = getFieldType(nodeName, props);
    const renderer = defaultValueRenderers[type];

    return renderer(props);
  }, [nodeName, props]);

  const fieldIsUsedInScore = useMemo(
    () =>
      isScoreSectionsContainsDataKey(props.dataKey, scoreSections) ||
      isVariableContainsDataKey(props.dataKey, variables),
    [props.dataKey, scoreSections, variables],
  );

  const isFiledWithOptions = isFieldWithOptions(props);

  return (
    <PanelContainer
      header={t('Header')}
      expanded={!!optionWeightErrorMessage || !!noAnswerScoreErrorMessage}
      panelName="Answers"
    >
      {hasSection(sections, 'noAnswerScore') && (
        <ControlsGroup contentClassName={Style.flexContainer}>
          <AppCheckboxInput
            label={t('EnableScore')}
            checked={scoreEnabled}
            disabled={fieldIsUsedInScore}
            // tooltip={fieldIsUsedInScore ? t('DisableScoreCheckboxTooltip') : undefined}
            onChange={(e) => {
              setScoreEnabled(e.target.checked);
              if (!e.target.checked) {
                setProp((p: FormFieldWithScoreProps & FormFieldWithOptionsProps) => {
                  p.noAnswerScore = undefined;
                  if (p.options?.length)
                    p.options = [
                      ...p.options.map((x) => ({
                        ...x,
                        scoreWeight: undefined,
                      })),
                    ];
                }, 500);
              }
            }}
          />
        </ControlsGroup>
      )}
      {isFiledWithOptions && <OptionsSubPanel errorMessage={optionWeightErrorMessage} scoreEnabled={scoreEnabled} />}
      {hasSection(sections, 'noAnswerScore') && scoreEnabled && (
        <div className={clsx(Style.gridContainer, Style.noAnswerScoreContainer)}>
          <div>{t('NoAnswerText')}</div>
          <SettingsInput
            type={'number'}
            tooltipText={t('NoAnswerScoreTooltip')}
            placeholder={t('ScoreWeightPlaceholder')}
            error={noAnswerScoreErrorMessage}
            value={props.noAnswerScore}
            inputClassName={Style.noAnswerScoreField}
            onChange={(e) => {
              setProp((p: FormFieldWithScoreProps) => {
                p.noAnswerScore = isNullOrEmpty(e.target.value) ? undefined : +e.target.value;
              }, 500);
            }}
          />
        </div>
      )}
      <ControlsGroup
        header={t('DefaultValue')}
        contentClassName={Style.flexContainer}
        collapsible={true}
        tooltip={t('DefaultValueTooltip')}
        expanded={props.defaultValue !== undefined && props.defaultValue !== null}
        onClose={() => {
          setProp((p: FormFieldProps) => {
            p.defaultValue = undefined;
          }, 500);
        }}
      >
        <div className={Style.defaultValueContainer}>{defaultValueComponent}</div>
      </ControlsGroup>
    </PanelContainer>
  );
};

const OptionsSubPanel: FC<{
  errorMessage?: string;
  scoreEnabled?: boolean;
}> = ({ errorMessage, scoreEnabled }) => {
  const { t } = useScopedTranslation('GeneralFormBuilder.Panels.Answers');
  const {
    actions: { setProp },
    props,
  } = useNode((node) => ({
    props: node.data.props as FormFieldWithOptionsProps,
  }));

  const endOfOptionsList = useRef<HTMLDivElement>(null);

  const addOptionHandler = useCallback(() => {
    setProp((p: FormFieldWithOptionsProps) => {
      p.options = [
        ...p.options,
        {
          key: createId(),
          value: `Option ${p.options.length + 1}`,
        },
      ];
    });

    endOfOptionsList.current?.scrollIntoView({ behavior: 'smooth' });
  }, [setProp]);

  const deleteOptionHandler = useCallback(
    (key: string | number) => {
      setProp((p: FormFieldWithOptionsProps) => {
        p.options = p.options.filter((o) => o.key !== key);
      });
    },
    [setProp],
  );

  const changeOptionValue = useCallback(
    (key: string | number, newValue: string) => {
      setProp((p: FormFieldWithOptionsProps) => {
        const i = p.options.findIndex((o) => o.key === key);
        p.options[i].value = newValue;
      }, 500);
    },
    [setProp],
  );

  const changeOptionScoreWeight = useCallback(
    (key: string | number, newValue: number | undefined) => {
      setProp((p: FormFieldWithOptionsProps) => {
        const i = p.options.findIndex((o) => o.key === key);
        p.options[i].scoreWeight = newValue;
      }, 500);
    },
    [setProp],
  );

  const moveOptionHandler = useCallback(
    (dragKey: string | number, hoverKey: string | number) => {
      setProp((p: FormFieldWithOptionsProps) => {
        const fromIndex = p.options.findIndex((o) => o.key === dragKey);
        const toIndex = p.options.findIndex((o) => o.key === hoverKey);
        const option = p.options[fromIndex];
        p.options.splice(fromIndex, 1);
        p.options.splice(toIndex, 0, option);
      }, 500);
    },
    [setProp],
  );

  return (
    <div className={Style.optionsContainerWithButton}>
      <DndProvider backend={ModifiedBackend}>
        <div className={Style.optionsContainer}>
          {props.options.map((option) => (
            <React.Fragment key={option.key}>
              <OptionCard
                option={option}
                scoreEnabled={scoreEnabled}
                moveOption={moveOptionHandler}
                deleteOption={deleteOptionHandler}
                changeOptionValue={changeOptionValue}
                changeOptionScoreWeight={changeOptionScoreWeight}
                scoreWeightErrorMessage={option.scoreWeight === undefined ? errorMessage : undefined}
              />
            </React.Fragment>
          ))}
          {/* Anchor to scroll */}
          <div ref={endOfOptionsList} />
        </div>
      </DndProvider>
      <AppButton
        variant={'icon-link'}
        colorSchema={'primary'}
        Icon={Plus}
        text={t('AddOption')}
        className={Style.addOptionButton}
        onClick={addOptionHandler}
      />
    </div>
  );
};

export type OptionCardProps = {
  option: FormFieldOption;
  scoreWeightErrorMessage?: string;
  scoreEnabled?: boolean;
  moveOption: (dragKey: number | string, hoverKey: number | string) => void;
  deleteOption: (key: string | number) => void;
  changeOptionValue: (key: string | number, newValue: string) => void;
  changeOptionScoreWeight?: (key: string | number, newValue: number | undefined) => void;
};

export const OptionCard: FC<OptionCardProps> = ({
  option,
  moveOption,
  deleteOption,
  changeOptionValue,
  changeOptionScoreWeight,
  scoreWeightErrorMessage,
  scoreEnabled,
}) => {
  const ref = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const [focused, setFocused] = useState(false);
  const { t } = useScopedTranslation('GeneralFormBuilder.Panels.Answers');

  // This effect allow text selection in the textinput
  useEffect(() => {
    ref.current?.setAttribute('draggable', (!focused).toString());
  }, [focused]);

  const [{ opacity }, drag] = useDrag({
    type: 'OptionCard',
    item: { key: option.key },
    collect: (monitor: DragSourceMonitor) => ({
      opacity: monitor.isDragging() ? 0 : 1,
    }),
  });

  const [, drop] = useDrop(() => ({
    accept: 'OptionCard',
    hover(item: { key: number | string }) {
      if (!ref.current) {
        return;
      }

      const dragKey = item.key;
      const hoverKey = option.key;
      if (dragKey === hoverKey) {
        return;
      }

      moveOption(dragKey, hoverKey);
    },
  }));

  drag(drop(ref));

  return (
    <div ref={ref} style={{ opacity }} className={Style.optionItem}>
      <div className={Style.flex}>
        <DragAndDrop className={Style.dragAndDropIcon} />
        <SettingsInput
          ref={inputRef}
          inputClassName={Style.valueInput}
          placeholder={t('OptionNamePlaceholder')}
          defaultValue={option.value}
          onFocus={() => setFocused(true)}
          onChange={(e) => {
            changeOptionValue(option.key, e.target.value);
            setFocused(false);
          }}
        />
        {scoreEnabled && (
          <SettingsInput
            type={'number'}
            placeholder={t('ScoreWeightPlaceholder')}
            defaultValue={option.scoreWeight}
            onFocus={() => setFocused(true)}
            className={Style.scoreInput}
            error={scoreWeightErrorMessage}
            hideErrorText={true}
            onChange={(e) => {
              changeOptionScoreWeight?.(option.key, isNullOrEmpty(e.target.value) ? undefined : +e.target.value);
              setFocused(false);
            }}
          />
        )}
        <AppButton variant={'icon-link'} colorSchema={'basic'} Icon={Delete} onClick={() => deleteOption(option.key)} />
      </div>
    </div>
  );
};
